// ─── OrgAdminView — menu "Administração" (self-service pelo manager) ──────────
//
// Gestão, DENTRO do planner, dos UTILIZADORES e DEPARTAMENTOS da PRÓPRIA
// organização. Dois separadores, cada um gated pelo seu módulo-filho:
//   · org_users        → Gerir Utilizadores
//   · org_departments  → Gerir Departamentos
//
// Âmbito à própria org é garantido no backend (/planner-api/org/*, requireManager
// + clientId = req.user.clientId). Aqui o ecrã só se mostra a quem tem o módulo.
//
// Visual: identidade ecooGPS (gradiente de marca) + temas claro/escuro
// (usePlannerTheme / usePlannerDialogTheme), cartões de resumo, estados vazios
// ricos e avatares — para um aspeto profissional, não "simples".
import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, Tabs, Tab, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, FormControlLabel, Switch,
  Checkbox, Snackbar,
} from '@material-ui/core';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import PeopleIcon from '@material-ui/icons/People';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ApartmentIcon from '@material-ui/icons/Apartment';
import MapIcon from '@material-ui/icons/Map';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';
import { usePlannerTheme, usePlannerDialogTheme } from '../../theme/plannerTheme';

// Identidade ecooGPS — gradiente de marca (igual ao Login / página de download).
const BRAND = 'linear-gradient(100deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
const BRAND_DARK = 'linear-gradient(100deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';
const ON_BRAND = '#03121c';

function palette(dark) {
  return dark
    ? { page: '#0b1a22', card: '#122133', ink: '#eaf2f6', muted: '#9fb3c0', soft: '#6f93a6',
        border: 'rgba(110,193,228,0.14)', headBg: 'rgba(255,255,255,0.03)', rowHover: 'rgba(255,255,255,0.035)',
        tile: 'rgba(255,255,255,0.03)', shadow: '0 12px 34px rgba(0,0,0,0.45)', brand: BRAND_DARK }
    : { page: '#eef4f8', card: '#ffffff', ink: '#0f2733', muted: '#5b7384', soft: '#94a3b8',
        border: '#e5eef3', headBg: '#f3f8fb', rowHover: '#f6fafc',
        tile: '#f5fafc', shadow: '0 12px 34px rgba(15,39,51,0.08)', brand: BRAND };
}

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    background: ({ dark }) => dark
      ? 'radial-gradient(1100px 440px at 80% -10%, rgba(47,213,150,0.08), transparent 60%), #0b1a22'
      : 'radial-gradient(1100px 440px at 80% -10%, rgba(47,213,150,0.10), transparent 60%), #eef4f8' },
  header: { display: 'flex', alignItems: 'center', gap: 15, padding: '20px 28px',
    background: ({ dark }) => dark ? 'rgba(18,33,44,0.86)' : 'rgba(255,255,255,0.86)',
    backdropFilter: 'blur(8px)',
    borderBottom: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.14)' : '1px solid #e5eef3' },
  headerIcon: { width: 46, height: 46, borderRadius: 13, flexShrink: 0,
    background: ({ dark }) => dark ? BRAND_DARK : BRAND,
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: ON_BRAND,
    boxShadow: '0 8px 20px rgba(24,189,238,0.38)' },
  title: { fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.02em',
    color: ({ dark }) => dark ? '#eaf2f6' : '#0f2733' },
  subtitle: { fontSize: '0.78rem', marginTop: 1, color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384' },
  tabsBar: { paddingLeft: 16,
    background: ({ dark }) => dark ? 'rgba(18,33,44,0.6)' : 'rgba(255,255,255,0.6)',
    borderBottom: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.12)' : '1px solid #e5eef3' },
  tab: { textTransform: 'none', fontWeight: 700, fontSize: '0.84rem', minHeight: 52,
    color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384' },
  content: { flex: 1, overflowY: 'auto', padding: 26 },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  count: { fontWeight: 800, fontSize: '0.98rem', color: ({ dark }) => dark ? '#eaf2f6' : '#0f2733' },
  tableWrap: { borderRadius: 16, overflow: 'hidden',
    background: ({ dark }) => dark ? '#122133' : '#ffffff',
    border: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.14)' : '1px solid #e5eef3',
    boxShadow: ({ dark }) => dark ? '0 12px 34px rgba(0,0,0,0.4)' : '0 12px 34px rgba(15,39,51,0.07)' },
  th: { fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em',
    color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384',
    background: ({ dark }) => dark ? 'rgba(255,255,255,0.03)' : '#f3f8fb',
    borderBottom: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.14)' : '1px solid #e5eef3' },
  td: { fontSize: '0.82rem', color: ({ dark }) => dark ? '#d6e3ee' : '#1e2f3a',
    borderBottom: ({ dark }) => dark ? '1px solid rgba(110,193,228,0.08)' : '1px solid #eef4f8' },
  row: { transition: 'background 0.12s',
    '&:hover': { background: ({ dark }) => dark ? 'rgba(255,255,255,0.035)' : '#f6fafc' } },
  modGroup: { marginTop: 10, marginBottom: 4, fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.04em', color: ({ dark }) => dark ? '#9fb3c0' : '#5b7384' },
}));

const PERM_LABELS = { viewer: 'Leitura', operator: 'Operação', manager: 'Gestor' };
const SCOPE_LABELS = { org: 'Toda a organização', zone: 'Só a zona', own: 'Só os próprios' };
const PERM_OPTS = ['viewer', 'operator', 'manager'];
const SCOPE_OPTS = ['org', 'zone', 'own'];
const PERM_COLORS = {
  viewer: { bg: 'rgba(100,116,139,0.16)', color: '#64748b' },
  operator: { bg: 'rgba(24,189,238,0.16)', color: '#0a86b8' },
  manager: { bg: 'rgba(47,213,150,0.18)', color: '#0a7f5f' },
};

// ── Botão primário com gradiente de marca ──────────────────────────────
function BrandButton({ dark, children, ...props }) {
  return (
    <Button {...props} variant="contained" disableElevation startIcon={<AddIcon />}
      style={{ background: dark ? BRAND_DARK : BRAND, color: ON_BRAND, fontWeight: 800,
        textTransform: 'none', borderRadius: 10, padding: '8px 18px',
        boxShadow: '0 8px 20px rgba(24,189,238,0.32)', ...(props.style || {}) }}>
      {children}
    </Button>
  );
}

// ── Avatar de iniciais ─────────────────────────────────────────────────
function Avatar({ name, dark }) {
  const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  return (
    <Box style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontWeight: 800, fontSize: '0.72rem', color: ON_BRAND,
      background: dark ? BRAND_DARK : BRAND, boxShadow: '0 4px 10px rgba(24,189,238,0.3)' }}>
      {initials}
    </Box>
  );
}

// ── Cartão de resumo (stat tile) ───────────────────────────────────────
function StatTile({ icon, value, label, pal, accent }) {
  return (
    <Box style={{ flex: '1 1 150px', minWidth: 140, display: 'flex', alignItems: 'center', gap: 12,
      background: pal.card, border: `1px solid ${pal.border}`, borderRadius: 14, padding: '14px 16px',
      boxShadow: pal.shadow }}>
      <Box style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: accent?.bg || pal.tile, color: accent?.color || pal.muted, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box style={{ minWidth: 0 }}>
        <Typography style={{ fontSize: '1.3rem', fontWeight: 800, lineHeight: 1, color: pal.ink }}>{value}</Typography>
        <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: pal.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 3 }}>{label}</Typography>
      </Box>
    </Box>
  );
}

// ── Estado vazio rico ──────────────────────────────────────────────────
function EmptyState({ icon, title, hint, pal, action }) {
  return (
    <Box style={{ padding: '48px 24px', textAlign: 'center' }}>
      <Box style={{ width: 64, height: 64, margin: '0 auto 14px', borderRadius: 18, display: 'flex',
        alignItems: 'center', justifyContent: 'center', background: pal.tile, color: pal.soft }}>
        {icon}
      </Box>
      <Typography style={{ fontSize: '0.98rem', fontWeight: 800, color: pal.ink }}>{title}</Typography>
      <Typography style={{ fontSize: '0.82rem', color: pal.muted, maxWidth: 420, margin: '6px auto 0', lineHeight: 1.5 }}>{hint}</Typography>
      {action && <Box style={{ marginTop: 18 }}>{action}</Box>}
    </Box>
  );
}

function OrgAdminView({ lang = 'pt' }) {
  const dark = usePlannerTheme() === 'dark';
  const classes = useStyles({ dark });
  const pal = palette(dark);
  const { token, modules } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const hasMod = (k) => !modules || modules.length === 0 || modules.includes(k);
  const canUsers = hasMod('org_users');
  const canDepts = hasMod('org_departments');
  const canZones = canUsers || canDepts; // Zonas: disponível a quem gere a org

  const [tab, setTab] = useState(canUsers ? 'users' : 'departments');
  const [options, setOptions] = useState({ zones: [], modules: [] });
  const [toast, setToast] = useState('');

  // Recarregável: ao criar/editar/apagar zonas, atualiza os pickers de zona.
  const loadOptions = useCallback(() => {
    fetch('/planner-api/org/options', { headers })
      .then(r => r.ok ? r.json() : { zones: [], modules: [] })
      .then(setOptions)
      .catch(() => setOptions({ zones: [], modules: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { if (token) loadOptions(); }, [token, loadOptions]);

  const zoneName = (id) => { const z = options.zones.find(z => z.id === id); return z ? z.name : '—'; };

  return (
    <Box className={classes.root} data-planner-theme={dark ? 'dark' : 'light'}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}><SupervisorAccountIcon /></Box>
        <Box>
          <Typography className={classes.title}>Administração</Typography>
          <Typography className={classes.subtitle}>Gestão de utilizadores, departamentos e zonas da organização</Typography>
        </Box>
      </Box>

      <Box className={classes.tabsBar}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} TabIndicatorProps={{ style: { height: 3, borderRadius: 3, background: dark ? '#20C6F4' : '#18BDEE' } }}>
          {canUsers && <Tab value="users" className={classes.tab} label="Utilizadores" icon={<PeopleIcon style={{ fontSize: 18 }} />} />}
          {canDepts && <Tab value="departments" className={classes.tab} label="Departamentos" icon={<AccountTreeIcon style={{ fontSize: 18 }} />} />}
          {canZones && <Tab value="zones" className={classes.tab} label="Zonas" icon={<MapIcon style={{ fontSize: 18 }} />} />}
        </Tabs>
      </Box>

      <Box className={classes.content}>
        {tab === 'users' && canUsers && (
          <UsersTab classes={classes} pal={pal} dark={dark} headers={headers} options={options} zoneName={zoneName} notify={setToast} />
        )}
        {tab === 'departments' && canDepts && (
          <DepartmentsTab classes={classes} pal={pal} dark={dark} headers={headers} options={options} zoneName={zoneName} notify={setToast} />
        )}
        {tab === 'zones' && canZones && (
          <ZonesTab classes={classes} pal={pal} dark={dark} headers={headers} notify={setToast} onChanged={loadOptions} />
        )}
      </Box>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ════════════ UTILIZADORES ════════════
function UsersTab({ classes, pal, dark, headers, options, zoneName, notify }) {
  const [list, setList] = useState(null);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null); // objeto user OU {} (novo)
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    fetch('/planner-api/org/users', { headers }).then(r => r.ok ? r.json() : []).then(setList).catch(() => setList([]));
    fetch('/planner-api/org/departments', { headers }).then(r => r.ok ? r.json() : []).then(setDepts).catch(() => setDepts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const deptName = (id) => { const d = depts.find(d => d.id === id); return d ? d.name : '—'; };

  async function remove(u) {
    const res = await fetch(`/planner-api/org/users/${u.id}`, { method: 'DELETE', headers });
    if (res.ok) { notify('Utilizador desativado'); load(); }
    else { const e = await res.json().catch(() => ({})); notify(e.error || 'Erro ao desativar'); }
    setConfirm(null);
  }

  if (list === null) return <Box textAlign="center" p={5}><CircularProgress /></Box>;

  const active = list.filter(u => u.isActive).length;
  const withDept = list.filter(u => u.departmentId).length;

  return (
    <>
      <Box style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <StatTile pal={pal} icon={<PeopleIcon />} value={list.length} label="Utilizadores" accent={{ bg: 'rgba(24,189,238,0.16)', color: '#0a86b8' }} />
        <StatTile pal={pal} icon={<CheckCircleIcon />} value={active} label="Ativos" accent={{ bg: 'rgba(47,213,150,0.18)', color: '#0a7f5f' }} />
        <StatTile pal={pal} icon={<AccountTreeIcon />} value={withDept} label="Com departamento" accent={{ bg: 'rgba(99,102,241,0.16)', color: '#6366f1' }} />
      </Box>

      <Box className={classes.toolbar}>
        <Typography className={classes.count}>{list.length} utilizador(es)</Typography>
        <BrandButton dark={dark} onClick={() => setEditing({})}>Novo utilizador</BrandButton>
      </Box>

      <Box className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Nome', 'Email', 'Permissão', 'Departamento', 'Zona', 'Âmbito', 'Origem', 'Estado'].map(h => (
                <TableCell key={h} className={classes.th}>{h}</TableCell>
              ))}
              <TableCell className={classes.th} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={9} style={{ border: 'none' }}>
                <EmptyState pal={pal} icon={<PeopleIcon style={{ fontSize: 30 }} />}
                  title="Ainda não há utilizadores"
                  hint="Cria o primeiro utilizador da tua organização. Podes definir a permissão, a zona e o departamento de cada um."
                  action={<BrandButton dark={dark} onClick={() => setEditing({})}>Novo utilizador</BrandButton>} />
              </TableCell></TableRow>
            )}
            {list.map(u => {
              const pc = PERM_COLORS[u.permissionLevel] || PERM_COLORS.viewer;
              return (
                <TableRow key={u.id} className={classes.row}>
                  <TableCell className={classes.td}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={u.name} dark={dark} />
                      <span style={{ fontWeight: 700 }}>{u.name}</span>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.td}>{u.email}</TableCell>
                  <TableCell className={classes.td}><Chip size="small" label={PERM_LABELS[u.permissionLevel] || u.permissionLevel} style={{ background: pc.bg, color: pc.color, fontWeight: 700 }} /></TableCell>
                  <TableCell className={classes.td}>{deptName(u.departmentId)}</TableCell>
                  <TableCell className={classes.td}>{u.zoneId ? zoneName(u.zoneId) : '—'}</TableCell>
                  <TableCell className={classes.td}>{SCOPE_LABELS[u.dataScope] || u.dataScope}</TableCell>
                  <TableCell className={classes.td}><Chip size="small" variant="outlined" label={u.provider === 'local' ? 'Local' : 'EcooGPS'} style={{ borderColor: pal.border, color: pal.muted }} /></TableCell>
                  <TableCell className={classes.td}>
                    {u.isActive
                      ? <Chip size="small" style={{ background: 'rgba(47,213,150,0.18)', color: '#0a7f5f', fontWeight: 700 }} label="Ativo" />
                      : <Chip size="small" style={{ background: 'rgba(239,68,68,0.16)', color: '#dc2626', fontWeight: 700 }} label="Inativo" />}
                  </TableCell>
                  <TableCell className={classes.td} align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(u)} style={{ color: pal.muted }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    {u.isActive && <Tooltip title="Desativar"><IconButton size="small" onClick={() => setConfirm(u)} style={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {editing && (
        <UserDialog user={editing} depts={depts} zones={options.zones} headers={headers}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { notify(msg); setEditing(null); load(); }} />
      )}
      <ConfirmDialog open={!!confirm} title="Desativar utilizador"
        message={confirm ? `Desativar "${confirm.name}"? Deixa de poder entrar; pode reativá-lo depois.` : ''}
        confirmLabel="Desativar" severity="warning" onConfirm={() => remove(confirm)} onClose={() => setConfirm(null)} />
    </>
  );
}

function UserDialog({ user, depts, zones, headers, onClose, onSaved }) {
  const dark = usePlannerTheme() === 'dark';
  const dialogTheme = usePlannerDialogTheme();
  const isNew = !user.id;
  const [form, setForm] = useState({
    name: user.name || '', email: user.email || '', password: '',
    permissionLevel: user.permissionLevel || 'operator',
    departmentId: user.departmentId || '', zoneId: user.zoneId || '',
    dataScope: user.dataScope || 'org', isActive: user.isActive !== false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Ao escolher departamento num utilizador NOVO, herda os defaults (o operador
  // pode sobrepor). Espelha o backend, que também aplica os defaults do dept.
  const onPickDept = (e) => {
    const id = e.target.value;
    const d = depts.find(d => d.id === id);
    setForm(f => ({
      ...f, departmentId: id,
      ...(isNew && d ? { permissionLevel: d.permissionLevel || f.permissionLevel, dataScope: d.defaultDataScope || f.dataScope, zoneId: d.defaultZoneId || f.zoneId } : {}),
    }));
  };

  async function save() {
    setErr(''); setSaving(true);
    const body = {
      name: form.name, permissionLevel: form.permissionLevel,
      departmentId: form.departmentId || null, zoneId: form.zoneId || null,
      dataScope: form.dataScope, isActive: form.isActive,
    };
    if (isNew) { body.email = form.email; body.password = form.password; }
    else if (form.password) body.password = form.password;
    const url = isNew ? '/planner-api/org/users' : `/planner-api/org/users/${user.id}`;
    const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers, body: JSON.stringify(body) });
    setSaving(false);
    if (res.ok) onSaved(isNew ? 'Utilizador criado' : 'Utilizador atualizado');
    else { const e = await res.json().catch(() => ({})); setErr(e.error || 'Erro ao gravar'); }
  }

  return (
    <ThemeProvider theme={dialogTheme}>
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} PaperProps={{ 'data-planner-theme': dark ? 'dark' : 'light', style: { borderRadius: 16, backgroundImage: 'none' } }}>
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8 }}>
        <Box style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: dark ? BRAND_DARK : BRAND, color: ON_BRAND }}>
          <PeopleIcon style={{ fontSize: 20 }} />
        </Box>
        <Typography style={{ fontSize: '1.05rem', fontWeight: 800 }}>{isNew ? 'Novo utilizador' : `Editar ${user.name}`}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth margin="dense" variant="outlined" label="Nome" value={form.name} onChange={set('name')} autoFocus />
        <TextField fullWidth margin="dense" variant="outlined" label="Email" value={form.email} onChange={set('email')} disabled={!isNew}
          helperText={isNew ? 'Serve de login (utilizador local)' : 'O email não pode ser alterado'} />
        <TextField fullWidth margin="dense" variant="outlined" type="password" label={isNew ? 'Palavra-passe' : 'Nova palavra-passe (deixar vazio = manter)'}
          value={form.password} onChange={set('password')} InputProps={{ startAdornment: <VpnKeyIcon style={{ fontSize: 16, color: '#94a3b8', marginRight: 6 }} /> }} />
        <TextField select fullWidth margin="dense" variant="outlined" label="Departamento" value={form.departmentId} onChange={onPickDept}>
          <MenuItem value="">(Sem departamento)</MenuItem>
          {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" variant="outlined" label="Permissão" value={form.permissionLevel} onChange={set('permissionLevel')}>
          {PERM_OPTS.map(p => <MenuItem key={p} value={p}>{PERM_LABELS[p]}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" variant="outlined" label="Zona" value={form.zoneId} onChange={set('zoneId')}>
          <MenuItem value="">(Todas / sem zona)</MenuItem>
          {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" variant="outlined" label="Âmbito de dados" value={form.dataScope} onChange={set('dataScope')}>
          {SCOPE_OPTS.map(s => <MenuItem key={s} value={s}>{SCOPE_LABELS[s]}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} color="primary" />} label="Ativo" />
        {err && <Typography color="error" style={{ fontSize: '0.8rem', marginTop: 8 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} style={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !form.name || (isNew && (!form.email || !form.password))}
          variant="contained" disableElevation
          style={{ background: dark ? BRAND_DARK : BRAND, color: ON_BRAND, fontWeight: 800, textTransform: 'none', borderRadius: 10, padding: '7px 20px' }}>
          {saving ? '...' : 'Gravar'}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
}

// ════════════ DEPARTAMENTOS ════════════
function DepartmentsTab({ classes, pal, dark, headers, options, zoneName, notify }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    fetch('/planner-api/org/departments', { headers }).then(r => r.ok ? r.json() : []).then(setList).catch(() => setList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(d) {
    const res = await fetch(`/planner-api/org/departments/${d.id}`, { method: 'DELETE', headers });
    if (res.ok) { notify('Departamento removido'); load(); }
    else { const e = await res.json().catch(() => ({})); notify(e.error || 'Erro ao remover'); }
    setConfirm(null);
  }

  if (list === null) return <Box textAlign="center" p={5}><CircularProgress /></Box>;

  const totalUsers = list.reduce((s, d) => s + (d.userCount || 0), 0);

  return (
    <>
      <Box style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <StatTile pal={pal} icon={<AccountTreeIcon />} value={list.length} label="Departamentos" accent={{ bg: 'rgba(24,189,238,0.16)', color: '#0a86b8' }} />
        <StatTile pal={pal} icon={<PeopleIcon />} value={totalUsers} label="Utilizadores atribuídos" accent={{ bg: 'rgba(47,213,150,0.18)', color: '#0a7f5f' }} />
      </Box>

      <Box className={classes.toolbar}>
        <Typography className={classes.count}>{list.length} departamento(s)</Typography>
        <BrandButton dark={dark} onClick={() => setEditing({})}>Novo departamento</BrandButton>
      </Box>

      <Box className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Nome', 'Módulos concedidos', 'Permissão base', 'Zona padrão', 'Âmbito padrão'].map(h => (
                <TableCell key={h} className={classes.th}>{h}</TableCell>
              ))}
              <TableCell className={classes.th} align="center">Utilizadores</TableCell>
              <TableCell className={classes.th} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={7} style={{ border: 'none' }}>
                <EmptyState pal={pal} icon={<ApartmentIcon style={{ fontSize: 30 }} />}
                  title="Ainda não há departamentos"
                  hint="Cria o primeiro para agrupar utilizadores e definir que módulos, permissão e zona têm por omissão."
                  action={<BrandButton dark={dark} onClick={() => setEditing({})}>Novo departamento</BrandButton>} />
              </TableCell></TableRow>
            )}
            {list.map(d => {
              const pc = PERM_COLORS[d.permissionLevel] || PERM_COLORS.viewer;
              return (
                <TableRow key={d.id} className={classes.row}>
                  <TableCell className={classes.td} style={{ fontWeight: 700 }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Box style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: pal.tile, color: pal.muted, flexShrink: 0 }}>
                        <AccountTreeIcon style={{ fontSize: 17 }} />
                      </Box>
                      {d.name}
                    </Box>
                  </TableCell>
                  <TableCell className={classes.td}>{(d.moduleKeys || []).length === 0 ? <span style={{ color: pal.soft }}>Todos os da org</span> : `${d.moduleKeys.length} módulo(s)`}</TableCell>
                  <TableCell className={classes.td}><Chip size="small" label={PERM_LABELS[d.permissionLevel] || d.permissionLevel} style={{ background: pc.bg, color: pc.color, fontWeight: 700 }} /></TableCell>
                  <TableCell className={classes.td}>{d.defaultZoneId ? zoneName(d.defaultZoneId) : '—'}</TableCell>
                  <TableCell className={classes.td}>{SCOPE_LABELS[d.defaultDataScope] || d.defaultDataScope}</TableCell>
                  <TableCell className={classes.td} align="center"><Chip size="small" label={d.userCount ?? 0} style={{ background: pal.tile, color: pal.ink, fontWeight: 700 }} /></TableCell>
                  <TableCell className={classes.td} align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(d)} style={{ color: pal.muted }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Remover"><IconButton size="small" onClick={() => setConfirm(d)} style={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {editing && (
        <DepartmentDialog dept={editing} zones={options.zones} availableModules={options.modules} headers={headers}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { notify(msg); setEditing(null); load(); }} />
      )}
      <ConfirmDialog open={!!confirm} title="Remover departamento"
        message={confirm ? `Remover "${confirm.name}"? Os utilizadores ficam sem departamento (mantêm as permissões atuais).` : ''}
        confirmLabel="Remover" severity="error" onConfirm={() => remove(confirm)} onClose={() => setConfirm(null)} />
    </>
  );
}

function DepartmentDialog({ dept, zones, availableModules, headers, onClose, onSaved }) {
  const dark = usePlannerTheme() === 'dark';
  const dialogTheme = usePlannerDialogTheme();
  const classes = useStyles({ dark });
  const isNew = !dept.id;
  const [form, setForm] = useState({
    name: dept.name || '',
    moduleKeys: Array.isArray(dept.moduleKeys) ? dept.moduleKeys : [],
    permissionLevel: dept.permissionLevel || 'operator',
    defaultZoneId: dept.defaultZoneId || '',
    defaultDataScope: dept.defaultDataScope || 'org',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleModule = (key) => {
    setForm(f => ({ ...f, moduleKeys: f.moduleKeys.includes(key) ? f.moduleKeys.filter(k => k !== key) : [...f.moduleKeys, key] }));
  };

  // Agrupa módulos por pai para uma lista legível (pai = cabeçalho de menu).
  const parents = availableModules.filter(m => !m.parentId);
  const childrenOf = (pid) => availableModules.filter(m => m.parentId === pid);

  async function save() {
    setErr(''); setSaving(true);
    const body = {
      name: form.name, moduleKeys: form.moduleKeys,
      permissionLevel: form.permissionLevel,
      defaultZoneId: form.defaultZoneId || null, defaultDataScope: form.defaultDataScope,
    };
    const url = isNew ? '/planner-api/org/departments' : `/planner-api/org/departments/${dept.id}`;
    const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers, body: JSON.stringify(body) });
    setSaving(false);
    if (res.ok) onSaved(isNew ? 'Departamento criado' : 'Departamento atualizado');
    else { const e = await res.json().catch(() => ({})); setErr(e.error || 'Erro ao gravar'); }
  }

  return (
    <ThemeProvider theme={dialogTheme}>
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} PaperProps={{ 'data-planner-theme': dark ? 'dark' : 'light', style: { borderRadius: 16, backgroundImage: 'none' } }}>
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8 }}>
        <Box style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: dark ? BRAND_DARK : BRAND, color: ON_BRAND }}>
          <AccountTreeIcon style={{ fontSize: 20 }} />
        </Box>
        <Typography style={{ fontSize: '1.05rem', fontWeight: 800 }}>{isNew ? 'Novo departamento' : `Editar ${dept.name}`}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth margin="dense" variant="outlined" label="Nome" value={form.name} onChange={set('name')} autoFocus />
        <TextField select fullWidth margin="dense" variant="outlined" label="Permissão base" value={form.permissionLevel} onChange={set('permissionLevel')}
          helperText="Nível aplicado por omissão aos utilizadores deste departamento">
          {PERM_OPTS.map(p => <MenuItem key={p} value={p}>{PERM_LABELS[p]}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" variant="outlined" label="Zona padrão" value={form.defaultZoneId} onChange={set('defaultZoneId')}>
          <MenuItem value="">(Sem zona)</MenuItem>
          {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
        </TextField>
        <TextField select fullWidth margin="dense" variant="outlined" label="Âmbito de dados padrão" value={form.defaultDataScope} onChange={set('defaultDataScope')}>
          {SCOPE_OPTS.map(s => <MenuItem key={s} value={s}>{SCOPE_LABELS[s]}</MenuItem>)}
        </TextField>

        <Typography className={classes.modGroup} style={{ marginTop: 18 }}>Módulos concedidos</Typography>
        <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 6 }}>
          Sem nenhum selecionado, o departamento dá acesso a todos os módulos da organização. Selecionar restringe a estes.
        </Typography>
        {parents.length === 0 && <Typography style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Sem módulos disponíveis.</Typography>}
        {parents.map(p => {
          const kids = childrenOf(p.id);
          return (
            <Box key={p.key} style={{ marginBottom: 6 }}>
              <FormControlLabel control={<Checkbox size="small" color="primary" checked={form.moduleKeys.includes(p.key)} onChange={() => toggleModule(p.key)} />}
                label={<span style={{ fontWeight: 700 }}>{p.name}</span>} />
              {kids.length > 0 && (
                <Box style={{ paddingLeft: 26 }}>
                  {kids.map(c => (
                    <FormControlLabel key={c.key} control={<Checkbox size="small" color="primary" checked={form.moduleKeys.includes(c.key)} onChange={() => toggleModule(c.key)} />} label={<span style={{ fontSize: '0.85rem' }}>{c.name}</span>} />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
        {err && <Typography color="error" style={{ fontSize: '0.8rem', marginTop: 8 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} style={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !form.name}
          variant="contained" disableElevation
          style={{ background: dark ? BRAND_DARK : BRAND, color: ON_BRAND, fontWeight: 800, textTransform: 'none', borderRadius: 10, padding: '7px 20px' }}>
          {saving ? '...' : 'Gravar'}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
}

// ════════════ ZONAS ════════════
function ZonesTab({ classes, pal, dark, headers, notify, onChanged }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    fetch('/planner-api/org/zones', { headers }).then(r => r.ok ? r.json() : []).then(setList).catch(() => setList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(z) {
    const res = await fetch(`/planner-api/org/zones/${z.id}`, { method: 'DELETE', headers });
    if (res.ok) { notify('Zona removida'); load(); onChanged && onChanged(); }
    else { const e = await res.json().catch(() => ({})); notify(e.error || 'Erro ao remover'); }
    setConfirm(null);
  }

  if (list === null) return <Box textAlign="center" p={5}><CircularProgress /></Box>;
  const totalUse = list.reduce((s, z) => s + (z.userCount || 0) + (z.deptCount || 0), 0);

  return (
    <>
      <Box style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <StatTile pal={pal} icon={<MapIcon />} value={list.length} label="Zonas" accent={{ bg: 'rgba(24,189,238,0.16)', color: '#0a86b8' }} />
        <StatTile pal={pal} icon={<PeopleIcon />} value={totalUse} label="Com zona atribuída" accent={{ bg: 'rgba(47,213,150,0.18)', color: '#0a7f5f' }} />
      </Box>

      <Box className={classes.toolbar}>
        <Typography className={classes.count}>{list.length} zona(s)</Typography>
        <BrandButton dark={dark} onClick={() => setEditing({})}>Nova zona</BrandButton>
      </Box>

      <Box className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.th}>Nome</TableCell>
              <TableCell className={classes.th} align="center">Utilizadores</TableCell>
              <TableCell className={classes.th} align="center">Departamentos</TableCell>
              <TableCell className={classes.th} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={4} style={{ border: 'none' }}>
                <EmptyState pal={pal} icon={<MapIcon style={{ fontSize: 30 }} />}
                  title="Ainda não há zonas"
                  hint="Cria a primeira zona (uma área/etiqueta) para depois limitares o âmbito de utilizadores e departamentos a 'Só a zona'."
                  action={<BrandButton dark={dark} onClick={() => setEditing({})}>Nova zona</BrandButton>} />
              </TableCell></TableRow>
            )}
            {list.map(z => (
              <TableRow key={z.id} className={classes.row}>
                <TableCell className={classes.td} style={{ fontWeight: 700 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: pal.tile, color: pal.muted, flexShrink: 0 }}>
                      <MapIcon style={{ fontSize: 17 }} />
                    </Box>
                    {z.name}
                  </Box>
                </TableCell>
                <TableCell className={classes.td} align="center"><Chip size="small" label={z.userCount ?? 0} style={{ background: pal.tile, color: pal.ink, fontWeight: 700 }} /></TableCell>
                <TableCell className={classes.td} align="center"><Chip size="small" label={z.deptCount ?? 0} style={{ background: pal.tile, color: pal.ink, fontWeight: 700 }} /></TableCell>
                <TableCell className={classes.td} align="right">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(z)} style={{ color: pal.muted }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Remover"><IconButton size="small" onClick={() => setConfirm(z)} style={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {editing && (
        <ZoneDialog zone={editing} headers={headers}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { notify(msg); setEditing(null); load(); onChanged && onChanged(); }} />
      )}
      <ConfirmDialog open={!!confirm} title="Remover zona"
        message={confirm ? `Remover "${confirm.name}"? Os utilizadores e departamentos que a usavam ficam sem zona.` : ''}
        confirmLabel="Remover" severity="error" onConfirm={() => remove(confirm)} onClose={() => setConfirm(null)} />
    </>
  );
}

function ZoneDialog({ zone, headers, onClose, onSaved }) {
  const dark = usePlannerTheme() === 'dark';
  const dialogTheme = usePlannerDialogTheme();
  const isNew = !zone.id;
  const [name, setName] = useState(zone.name || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    setErr(''); setSaving(true);
    const url = isNew ? '/planner-api/org/zones' : `/planner-api/org/zones/${zone.id}`;
    const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers, body: JSON.stringify({ name }) });
    setSaving(false);
    if (res.ok) onSaved(isNew ? 'Zona criada' : 'Zona atualizada');
    else { const e = await res.json().catch(() => ({})); setErr(e.error || 'Erro ao gravar'); }
  }

  return (
    <ThemeProvider theme={dialogTheme}>
    <Dialog open fullWidth maxWidth="xs" onClose={onClose} PaperProps={{ 'data-planner-theme': dark ? 'dark' : 'light', style: { borderRadius: 16, backgroundImage: 'none' } }}>
      <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8 }}>
        <Box style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: dark ? BRAND_DARK : BRAND, color: ON_BRAND }}>
          <MapIcon style={{ fontSize: 20 }} />
        </Box>
        <Typography style={{ fontSize: '1.05rem', fontWeight: 800 }}>{isNew ? 'Nova zona' : `Editar ${zone.name}`}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <TextField fullWidth margin="dense" variant="outlined" label="Nome da zona" value={name} onChange={e => setName(e.target.value)} autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) save(); }} />
        {err && <Typography color="error" style={{ fontSize: '0.8rem', marginTop: 8 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} style={{ textTransform: 'none', fontWeight: 600 }}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !name.trim()} variant="contained" disableElevation
          style={{ background: dark ? BRAND_DARK : BRAND, color: ON_BRAND, fontWeight: 800, textTransform: 'none', borderRadius: 10, padding: '7px 20px' }}>
          {saving ? '...' : 'Gravar'}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
}

export default OrgAdminView;
